import type { FileAttachment, InteractionOutcome } from "@bash/shared";
import { apiUrl } from "./base";
import { getRequiredAccessToken } from "./client";

/**
 * A resolve the server refused: its HTTP status and its own `error` code, read
 * from the JSON body as a field (never from the text). A caller that needs the
 * reason reads `code`, e.g. "files_not_synced" (spec 2026-09-24: the box took
 * none of a file answer's files, so nothing resolved).
 */
export class ResolveInteractionError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    detail: string
  ) {
    super(`resolveInteraction failed: ${status} ${detail}`);
    this.name = "ResolveInteractionError";
  }
}

/** The body's `error` field, when the body is a JSON object that names one. */
function errorCode(body: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(body);
    if (typeof parsed !== "object" || parsed === null) return undefined;
    const { error } = parsed as { error?: unknown };
    return typeof error === "string" ? error : undefined;
  } catch {
    return undefined;
  }
}

/**
 * POST /api/interactions/:id/resolve
 *
 * Called from Submit/Skip flows as the canonical resolution path. During the
 * Phase 4 cutover this runs in parallel with the legacy INTERACTION_RESPONSE
 * socket emit. After Phase 9 the socket emit is removed and this is the sole path.
 *
 * Retries up to 3 times, with exponential backoff (250ms ×2), on transient
 * failures:
 *   - network error (fetch rejects) — the request never reached the server;
 *   - 5xx — a transient server-side error;
 *   - 404 — the fire-and-forget registerInteraction may not have committed to
 *     DB yet when the user clicks Submit (register race window).
 * A non-retryable 4xx (auth, validation, a 409 files_not_synced) throws
 * immediately, as a ResolveInteractionError carrying the server's code.
 */
export async function resolveInteractionApi(
  id: string,
  outcome: InteractionOutcome,
  files?: FileAttachment[]
): Promise<void> {
  const maxAttempts = 3;
  const baseDelayMs = 250;
  let lastError: Error | null = null;

  // Server's authenticateRequest expects `Authorization: Bearer <jwt>` — every
  // other authenticated endpoint in apps/web does the same. Cookies alone are
  // not honored.
  const accessToken = await getRequiredAccessToken(
    "resolveInteraction: no Supabase session"
  );

  const backoff = (attempt: number) =>
    new Promise((r) => setTimeout(r, baseDelayMs * 2 ** (attempt - 1)));

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let res: Response;
    try {
      res = await fetch(apiUrl(`/api/interactions/${id}/resolve`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
        // A file answer's files ride beside the outcome (spec 2026-09-24);
        // without any the body is today's, byte for byte.
        body: JSON.stringify(files?.length ? { outcome, files } : { outcome }),
      });
    } catch (err) {
      // Network error — the request never reached the server. Retry.
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < maxAttempts) {
        await backoff(attempt);
        continue;
      }
      break;
    }

    if (res.ok) return;

    // Transient: 404 register-race or any 5xx. Retry with backoff.
    if ((res.status === 404 || res.status >= 500) && attempt < maxAttempts) {
      await backoff(attempt);
      continue;
    }

    // Non-retryable 4xx (or a retryable status on the final attempt).
    const text = await res.text().catch(() => "");
    lastError = new ResolveInteractionError(
      res.status,
      errorCode(text),
      text.slice(0, 200)
    );
    break;
  }

  throw lastError ?? new Error("resolveInteraction failed: unknown");
}
